import { Extension } from "@tiptap/react";
import type { Editor } from "@tiptap/react";
import type { Node } from "@tiptap/pm/model";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import type { PluginView, Transaction } from "@tiptap/pm/state";
import { CellSelection, TableMap, moveTableColumn, moveTableRow } from "@tiptap/pm/tables";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { EditorView } from "@tiptap/pm/view";
import {
  clamp,
  domCellAround,
  getCellIndicesFromDOM,
  getColumnCells,
  getIndexCoordinates,
  getRowCells,
  getTableFromDOM,
  isHTMLElement,
  isTableNode,
  selectCellsByCoords,
} from "./table-utils";

// Ported from the mnemos table kit. A ProseMirror plugin tracks the pointer
// over tables and publishes which row/column is hovered (plus the DOMRects the
// floating handles need); it also owns row/column drag-and-drop with a drop
// indicator. The React side subscribes through editor.storage — the same
// bridge the slash menu uses, since extension closures can outlive a React
// instance across strict-mode remounts.

export type TableHandlesState = {
  block: Node;
  blockPos: number;
  colIndex: number | undefined;
  draggingState?: {
    draggedCellOrientation: "col" | "row";
    initialOffset: number;
    mousePos: number;
    originalIndex: number;
  };
  referencePosCell?: DOMRect;
  referencePosTable: DOMRect;
  rowIndex: number | undefined;
  show: boolean;
  showAddOrRemoveColumnsButton: boolean;
  showAddOrRemoveRowsButton: boolean;
};

export type TableHandlesStorage = {
  onStateChange: ((state: TableHandlesState) => void) | null;
  state: TableHandlesState | null;
  view: TableHandleView | null;
};

export const tableHandlePluginKey = new PluginKey("tableHandles");

function getStorage(editor: Editor): TableHandlesStorage | undefined {
  return (editor.storage as unknown as Record<string, TableHandlesStorage | undefined>).tableHandles;
}

function hideElements(selector: string, root: Document | ShadowRoot) {
  root.querySelectorAll<HTMLElement>(selector).forEach((element) => {
    element.style.visibility = "hidden";
  });
}

class TableHandleView implements PluginView {
  public editor: Editor;
  public editorView: EditorView;
  public state: TableHandlesState | undefined = undefined;
  public menuFrozen = false;
  public mouseState: "down" | "selecting" | "up" = "up";
  public tablePos: number | undefined;
  public tableElement: HTMLElement | undefined;

  constructor(editor: Editor, editorView: EditorView) {
    this.editor = editor;
    this.editorView = editorView;

    this.editorView.dom.addEventListener("mousemove", this.mouseMoveHandler);
    this.editorView.dom.addEventListener("mousedown", this.mouseDownHandler);
    this.editorView.dom.addEventListener("mouseleave", this.mouseLeaveHandler);
    window.addEventListener("mouseup", this.mouseUpHandler);
    // The handles are fixed-positioned portals: scrolling moves the table away
    // from under them, so any scroll dismisses them until the next hover.
    document.addEventListener("scroll", this.scrollHandler, true);
    this.editorView.root.addEventListener("dragover", this.dragOverHandler as EventListener);
    this.editorView.root.addEventListener("drop", this.dropHandler as EventListener);
  }

  emitUpdate = () => {
    if (!this.state) return;
    const storage = getStorage(this.editor);
    if (!storage) return;
    storage.state = this.state;
    storage.onStateChange?.(this.state);
  };

  // Clicking a cell while a CellSelection is active should return to a text
  // selection inside that cell instead of keeping the block highlight.
  private mouseDownHandler = (event: MouseEvent) => {
    this.mouseState = "down";

    const { state, view } = this.editor;
    if (!(state.selection instanceof CellSelection) || this.editor.isFocused) return;

    const posInfo = view.posAtCoords({ left: event.clientX, top: event.clientY });
    if (!posInfo) return;

    const $pos = state.doc.resolve(posInfo.pos);
    let paraDepth = -1;
    let inTableCell = false;
    for (let d = $pos.depth; d >= 0; d -= 1) {
      const node = $pos.node(d);
      if (!inTableCell && (node.type.name === "tableCell" || node.type.name === "tableHeader")) inTableCell = true;
      if (paraDepth === -1 && node.type.name === "paragraph") paraDepth = d;
      if (inTableCell && paraDepth !== -1) break;
    }
    if (!inTableCell || paraDepth === -1) return;

    const nextSel = TextSelection.create(state.doc, $pos.start(paraDepth), $pos.end(paraDepth));
    if (state.selection.eq(nextSel)) return;
    view.dispatch(state.tr.setSelection(nextSel));
    view.focus();
  };

  private mouseUpHandler = (event: MouseEvent) => {
    this.mouseState = "up";
    this.mouseMoveHandler(event);
  };

  private mouseMoveHandler = (event: MouseEvent) => {
    if (this.menuFrozen || this.mouseState === "selecting") return;
    const target = event.target;
    if (!isHTMLElement(target) || !this.editorView.dom.contains(target)) return;
    this.handleMouseMove(event);
  };

  // Leaving the editor hides the handles — unless the pointer is heading onto
  // the handles themselves (they live in a body portal, outside the editor).
  private mouseLeaveHandler = (event: MouseEvent) => {
    if (this.menuFrozen || this.state?.draggingState) return;
    const next = event.relatedTarget;
    if (next instanceof Element && next.closest("[data-table-handles]")) return;
    this.hideHandles();
  };

  private scrollHandler = () => {
    if (this.menuFrozen || this.state?.draggingState) return;
    this.hideHandles();
  };

  public hideHandles() {
    if (!this.state?.show) return;
    this.state = {
      ...this.state,
      colIndex: undefined,
      referencePosCell: undefined,
      rowIndex: undefined,
      show: false,
      showAddOrRemoveColumnsButton: false,
      showAddOrRemoveRowsButton: false,
    };
    this.emitUpdate();
  }

  private handleMouseMove(event: MouseEvent) {
    const around = domCellAround(event.target as Element);

    // Dragging text inside a cell should not chase the pointer with handles.
    if (around?.type === "cell" && this.mouseState === "down" && !this.state?.draggingState) {
      this.mouseState = "selecting";
      this.hideHandles();
      return;
    }

    if (!around || !this.editor.isEditable) {
      this.hideHandles();
      return;
    }

    const tbody = around.tbodyNode;
    if (!tbody) return;

    const tableRect = tbody.getBoundingClientRect();
    const coords = this.editor.view.posAtCoords({ left: event.clientX, top: event.clientY });
    if (!coords) return;

    const $pos = this.editor.view.state.doc.resolve(coords.pos);
    let blockInfo: { node: Node; pos: number } | undefined;
    for (let d = $pos.depth; d >= 0; d -= 1) {
      const node = $pos.node(d);
      if (isTableNode(node)) {
        blockInfo = { node, pos: d === 0 ? 0 : $pos.before(d) };
        break;
      }
    }
    if (!blockInfo || blockInfo.node.type.name !== "table") return;

    this.tableElement = (this.editor.view.nodeDOM(blockInfo.pos) as HTMLElement | null) ?? undefined;
    this.tablePos = blockInfo.pos;

    if (around.type === "wrapper") {
      // Hovering the wrapper padding around the table: keep whatever handles
      // are up, decide whether the edge "+" buttons should show.
      const below = event.clientY >= tableRect.bottom - 1 && event.clientY < tableRect.bottom + 20;
      const right = event.clientX >= tableRect.right - 1 && event.clientX < tableRect.right + 20;
      const beyondEdge = event.clientX > tableRect.right || event.clientY > tableRect.bottom;

      this.state = {
        ...this.state,
        block: blockInfo.node,
        blockPos: blockInfo.pos,
        colIndex: beyondEdge ? undefined : this.state?.colIndex,
        referencePosCell: beyondEdge ? undefined : this.state?.referencePosCell,
        referencePosTable: tableRect,
        rowIndex: beyondEdge ? undefined : this.state?.rowIndex,
        show: true,
        showAddOrRemoveColumnsButton: right,
        showAddOrRemoveRowsButton: below,
      };
    } else {
      const cellPosition = getCellIndicesFromDOM(around.domNode as HTMLTableCellElement, blockInfo.node, this.editor);
      if (!cellPosition) return;

      const { colIndex, rowIndex } = cellPosition;
      const lastRowIndex = blockInfo.node.content.childCount - 1;
      const lastColIndex = (blockInfo.node.content.firstChild?.content.childCount ?? 0) - 1;

      if (
        this.state?.show &&
        this.state.blockPos === blockInfo.pos &&
        this.state.rowIndex === rowIndex &&
        this.state.colIndex === colIndex
      ) {
        return;
      }

      this.state = {
        block: blockInfo.node,
        blockPos: blockInfo.pos,
        colIndex,
        draggingState: undefined,
        referencePosCell: (around.domNode as HTMLElement).getBoundingClientRect(),
        referencePosTable: tableRect,
        rowIndex,
        show: true,
        showAddOrRemoveColumnsButton: colIndex === lastColIndex,
        showAddOrRemoveRowsButton: rowIndex === lastRowIndex,
      };
    }

    this.emitUpdate();
  }

  private dragOverHandler = (event: DragEvent) => {
    if (this.state?.draggingState === undefined) return;

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";

    hideElements(".prosemirror-dropcursor-block, .prosemirror-dropcursor-inline", this.editorView.root);

    const { bottom, left, right, top } = this.state.referencePosTable;
    const boundedMouseCoords = {
      left: clamp(event.clientX, left + 1, right - 1),
      top: clamp(event.clientY, top + 1, bottom - 1),
    };

    const cellElement = this.editorView.root
      .elementsFromPoint(boundedMouseCoords.left, boundedMouseCoords.top)
      .find((element) => element.tagName === "TD" || element.tagName === "TH");
    if (!cellElement || !isHTMLElement(cellElement)) return;

    const cellPosition = getCellIndicesFromDOM(cellElement as HTMLTableCellElement, this.state.block, this.editor);
    if (!cellPosition) return;

    const { colIndex, rowIndex } = cellPosition;
    const isRow = this.state.draggingState.draggedCellOrientation === "row";
    const oldIndex = isRow ? this.state.rowIndex : this.state.colIndex;
    const newIndex = isRow ? rowIndex : colIndex;
    const mousePos = isRow ? boundedMouseCoords.top : boundedMouseCoords.left;

    const cellChanged = this.state.rowIndex !== rowIndex || this.state.colIndex !== colIndex;
    const mousePosChanged = this.state.draggingState.mousePos !== mousePos;

    if (cellChanged || mousePosChanged) {
      this.state = {
        ...this.state,
        colIndex,
        draggingState: { ...this.state.draggingState, mousePos },
        referencePosCell: cellElement.getBoundingClientRect(),
        rowIndex,
      };
      this.emitUpdate();
    }

    if (newIndex !== oldIndex) {
      // Nudge the decorations so the drop indicator follows the pointer.
      this.editor.view.dispatch(this.editor.state.tr.setMeta(tableHandlePluginKey, this.menuFrozen));
    }
  };

  private dropHandler = () => {
    this.mouseState = "up";

    const st = this.state;
    if (!st?.draggingState) return false;

    const { blockPos, colIndex, draggingState, rowIndex } = st;
    const isRow = draggingState.draggedCellOrientation === "row";
    const destIndex = isRow ? rowIndex : colIndex;
    if (typeof blockPos !== "number" || destIndex === undefined) return false;

    const orientation = isRow ? ("row" as const) : ("column" as const);
    const cellCoords = getIndexCoordinates(this.editor, draggingState.originalIndex, orientation, blockPos);
    if (!cellCoords) return false;

    const stateWithCellSel = selectCellsByCoords(this.editor, blockPos, cellCoords);
    if (!stateWithCellSel) return false;

    const dispatch = (tr: Transaction) => this.editor.view.dispatch(tr);
    const move = isRow ? moveTableRow : moveTableColumn;
    move({ from: draggingState.originalIndex, pos: blockPos + 1, select: true, to: destIndex })(stateWithCellSel, dispatch);

    this.state = { ...st, draggingState: undefined };
    this.emitUpdate();
    this.editor.view.dispatch(this.editor.state.tr.setMeta(tableHandlePluginKey, false));
    return true;
  };

  update(view: EditorView): void {
    const pluginState = tableHandlePluginKey.getState(view.state);
    if (pluginState !== undefined && pluginState !== this.menuFrozen) {
      this.menuFrozen = pluginState;
    }

    if (!this.state?.show) return;

    if (!this.tableElement?.isConnected) {
      this.hideHandles();
      return;
    }

    const tableInfo = getTableFromDOM(this.tableElement, this.editor);
    if (!tableInfo || tableInfo.node.type.name !== "table") {
      this.hideHandles();
      return;
    }

    const { height: rowCount, width: colCount } = TableMap.get(tableInfo.node);

    let newRowIndex = this.state.rowIndex;
    let newColIndex = this.state.colIndex;
    if (newRowIndex !== undefined && newRowIndex >= rowCount) newRowIndex = rowCount ? rowCount - 1 : undefined;
    if (newColIndex !== undefined && newColIndex >= colCount) newColIndex = colCount ? colCount - 1 : undefined;

    const tableBody = this.tableElement.querySelector("tbody");
    if (!tableBody) return;

    let newReferencePosCell = this.state.referencePosCell;
    if (newRowIndex !== undefined && newColIndex !== undefined) {
      const cellEl = tableBody.children[newRowIndex]?.children[newColIndex];
      if (cellEl) {
        newReferencePosCell = cellEl.getBoundingClientRect();
      } else {
        newRowIndex = undefined;
        newColIndex = undefined;
        newReferencePosCell = undefined;
      }
    }

    const newReferencePosTable = tableBody.getBoundingClientRect();
    const blockChanged = this.state.block !== tableInfo.node || this.state.blockPos !== tableInfo.pos;
    const indicesChanged = newRowIndex !== this.state.rowIndex || newColIndex !== this.state.colIndex;
    const refPosChanged =
      newReferencePosCell !== this.state.referencePosCell || newReferencePosTable !== this.state.referencePosTable;

    if (blockChanged || indicesChanged || refPosChanged) {
      this.state = {
        ...this.state,
        block: tableInfo.node,
        blockPos: tableInfo.pos,
        colIndex: newColIndex,
        referencePosCell: newReferencePosCell,
        referencePosTable: newReferencePosTable,
        rowIndex: newRowIndex,
      };
      this.emitUpdate();
    }
  }

  destroy(): void {
    this.editorView.dom.removeEventListener("mousemove", this.mouseMoveHandler);
    this.editorView.dom.removeEventListener("mousedown", this.mouseDownHandler);
    this.editorView.dom.removeEventListener("mouseleave", this.mouseLeaveHandler);
    window.removeEventListener("mouseup", this.mouseUpHandler);
    document.removeEventListener("scroll", this.scrollHandler, true);
    this.editorView.root.removeEventListener("dragover", this.dragOverHandler as EventListener);
    this.editorView.root.removeEventListener("drop", this.dropHandler as EventListener);

    const storage = getStorage(this.editor);
    if (storage?.view === this) storage.view = null;
  }
}

function buildDragDecorations(editor: Editor, view: TableHandleView | null, doc: Node): DecorationSet | null {
  if (!view?.state?.draggingState || view.tablePos === undefined) return null;

  const { draggingState } = view.state;
  const isRow = draggingState.draggedCellOrientation === "row";
  const newIndex = isRow ? view.state.rowIndex : view.state.colIndex;
  if (newIndex === undefined) return null;

  const decorations: Decoration[] = [];
  const { originalIndex } = draggingState;

  const originalCells = isRow
    ? getRowCells(editor, originalIndex, view.state.blockPos)
    : getColumnCells(editor, originalIndex, view.state.blockPos);
  originalCells.cells.forEach((cell) => {
    if (cell.node) {
      decorations.push(
        Decoration.node(cell.pos, cell.pos + cell.node.nodeSize, { class: "table-cell-dragging-source" }),
      );
    }
  });

  if (newIndex !== originalIndex) {
    const targetCells = isRow
      ? getRowCells(editor, newIndex, view.state.blockPos)
      : getColumnCells(editor, newIndex, view.state.blockPos);

    targetCells.cells.forEach((cell) => {
      if (!cell.node) return;
      const decorationPos = cell.pos + (newIndex > originalIndex ? cell.node.nodeSize - 2 : 2);
      decorations.push(
        Decoration.widget(decorationPos, () => {
          const widget = document.createElement("div");
          widget.className = "tiptap-table-dropcursor";
          if (isRow) {
            widget.style.left = "0";
            widget.style.right = "0";
            widget.style.height = "3px";
            // Cell borders are 1px; snap the 3px indicator over them.
            if (newIndex > originalIndex) widget.style.bottom = "-1px";
            else widget.style.top = "-1px";
          } else {
            widget.style.top = "0";
            widget.style.bottom = "0";
            widget.style.width = "3px";
            if (newIndex > originalIndex) widget.style.right = "-1px";
            else widget.style.left = "-1px";
          }
          return widget;
        }),
      );
    });
  }

  return DecorationSet.create(doc, decorations);
}

export const TableHandleExtension = Extension.create({
  name: "tableHandles",

  addStorage(): TableHandlesStorage {
    return { onStateChange: null, state: null, view: null };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    let pluginView: TableHandleView | null = null;

    return [
      new Plugin({
        key: tableHandlePluginKey,
        state: {
          init: () => false,
          apply: (tr, frozen: boolean) => {
            const meta = tr.getMeta(tableHandlePluginKey) as boolean | undefined;
            return meta !== undefined ? meta : frozen;
          },
        },
        view: (editorView) => {
          pluginView = new TableHandleView(editor, editorView);
          const storage = getStorage(editor);
          if (storage) storage.view = pluginView;
          return pluginView;
        },
        props: {
          decorations: (state) => buildDragDecorations(editor, pluginView, state.doc) ?? undefined,
        },
      }),
    ];
  },
});

export function hideTableHandles(editor: Editor) {
  const view = getStorage(editor)?.view;
  if (!view || view.menuFrozen || view.state?.draggingState) return;
  view.hideHandles();
}

export function freezeHandles(editor: Editor) {
  editor.view.dispatch(editor.state.tr.setMeta(tableHandlePluginKey, true));
}

export function unfreezeHandles(editor: Editor) {
  editor.view.dispatch(editor.state.tr.setMeta(tableHandlePluginKey, false));
}

// --- Drag image -------------------------------------------------------------

const DRAG_IMAGE_STYLE_PROPS = [
  "boxSizing",
  "backgroundColor",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderRadius",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "color",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "lineHeight",
  "letterSpacing",
  "textAlign",
  "verticalAlign",
  "whiteSpace",
  "width",
  "maxWidth",
  "height",
];

const toDash = (prop: string) => prop.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

function copyComputedStyles(source: HTMLElement, target: HTMLElement) {
  const cs = getComputedStyle(source);
  for (const prop of DRAG_IMAGE_STYLE_PROPS) {
    const value = cs.getPropertyValue(toDash(prop));
    if (value) target.style.setProperty(toDash(prop), value);
  }
  target.style.overflow = "hidden";
  target.style.textOverflow = "ellipsis";
  if (cs.whiteSpace === "" || cs.whiteSpace === "normal") target.style.whiteSpace = "nowrap";
}

function cloneWithStyles(root: HTMLElement): HTMLElement {
  const clone = root.cloneNode(true) as HTMLElement;
  const queue: Array<{ dst: Element; src: Element }> = [{ dst: clone, src: root }];
  while (queue.length) {
    const { dst, src } = queue.shift()!;
    if (src instanceof HTMLElement && dst instanceof HTMLElement) copyComputedStyles(src, dst);
    const srcChildren = Array.from(src.children);
    const dstChildren = Array.from(dst.children);
    for (let i = 0; i < Math.min(srcChildren.length, dstChildren.length); i += 1) {
      queue.push({ dst: dstChildren[i]!, src: srcChildren[i]! });
    }
  }
  return clone;
}

function lockCellWidth(fromCell: HTMLElement, toCell: HTMLElement) {
  const rect = fromCell.getBoundingClientRect();
  if (rect.width > 0) {
    toCell.style.width = `${rect.width}px`;
    toCell.style.maxWidth = `${rect.width}px`;
  }
}

function createTableDragImage(editor: Editor, orientation: "col" | "row", index: number, tablePos: number): HTMLElement {
  const editorRect = editor.view.dom.getBoundingClientRect();
  const wrapper = document.createElement("div");
  Object.assign(wrapper.style, {
    filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.18)) drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
    left: "-10000px",
    maxWidth: `${Math.max(0, editorRect.width)}px`,
    overflow: "hidden",
    pointerEvents: "none",
    position: "fixed",
    top: "-10000px",
    zIndex: "2147483647",
  });

  const tableEl = editor.view.nodeDOM(tablePos) as HTMLTableElement | null;
  const body = tableEl?.querySelector("tbody");
  if (!tableEl || !body) {
    document.body.appendChild(wrapper);
    return wrapper;
  }

  const tableClone = document.createElement("table");
  const tbodyClone = document.createElement("tbody");
  const tcs = getComputedStyle(tableEl);
  tableClone.style.borderCollapse = tcs.borderCollapse;
  tableClone.style.tableLayout = "fixed";
  tableClone.className = tableEl.className;

  if (orientation === "row") {
    const row = body.rows[index];
    if (row) {
      const rowClone = cloneWithStyles(row) as HTMLTableRowElement;
      for (let i = 0; i < row.cells.length; i += 1) {
        const dst = rowClone.cells[i];
        if (dst) lockCellWidth(row.cells[i] as HTMLElement, dst as HTMLElement);
      }
      tbodyClone.appendChild(rowClone);
    }
  } else {
    let firstCellWidth = 0;
    for (let r = 0; r < body.rows.length; r += 1) {
      const srcCell = body.rows[r]?.cells[index] as HTMLElement | undefined;
      if (!srcCell) continue;
      const tr = document.createElement("tr");
      const cellClone = cloneWithStyles(srcCell);
      if (!firstCellWidth) firstCellWidth = srcCell.getBoundingClientRect().width;
      lockCellWidth(srcCell, cellClone);
      tr.appendChild(cellClone);
      tbodyClone.appendChild(tr);
    }
    if (firstCellWidth > 0) {
      tableClone.style.width = `${firstCellWidth}px`;
      tableClone.style.maxWidth = `${firstCellWidth}px`;
    }
  }

  tableClone.appendChild(tbodyClone);
  wrapper.appendChild(tableClone);
  document.body.appendChild(wrapper);

  const rect = wrapper.getBoundingClientRect();
  if (rect.width > editorRect.width && rect.width > 0) {
    wrapper.style.transformOrigin = "top left";
    wrapper.style.transform = `scale(${editorRect.width / rect.width})`;
  }
  return wrapper;
}

// --- Handle drag start / end -------------------------------------------------

function tableDragStart(
  editor: Editor,
  orientation: "col" | "row",
  event: { clientX: number; clientY: number; currentTarget: EventTarget & Element; dataTransfer: DataTransfer | null },
) {
  const view = getStorage(editor)?.view;
  const state = view?.state;
  if (!view || !state) return;

  const index = orientation === "col" ? state.colIndex : state.rowIndex;
  if (index === undefined) return;

  const { blockPos, referencePosCell } = state;
  const mousePos = orientation === "col" ? event.clientX : event.clientY;

  // Clear an active CellSelection so the moved cells don't collapse the doc
  // positions the drag relies on.
  if (editor.state.selection instanceof CellSelection) {
    const safeSel = TextSelection.near(editor.state.doc.resolve(blockPos), 1);
    editor.view.dispatch(editor.state.tr.setSelection(safeSel));
  }

  const dragImage = createTableDragImage(editor, orientation, index, blockPos);
  if (event.dataTransfer) {
    const handleRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const offset = orientation === "col" ? { x: handleRect.width / 2, y: 0 } : { x: 0, y: handleRect.height / 2 };
    event.dataTransfer.effectAllowed = orientation === "col" ? "move" : "copyMove";
    event.dataTransfer.setDragImage(dragImage, offset.x, offset.y);
  }
  const cleanup = () => dragImage.parentNode?.removeChild(dragImage);
  document.addEventListener("drop", cleanup, { once: true });
  document.addEventListener("dragend", cleanup, { once: true });

  const initialOffset = referencePosCell
    ? (orientation === "col" ? referencePosCell.left : referencePosCell.top) - mousePos
    : 0;

  view.state = {
    ...state,
    draggingState: { draggedCellOrientation: orientation, initialOffset, mousePos, originalIndex: index },
  };
  view.emitUpdate();
  editor.view.dispatch(editor.state.tr.setMeta(tableHandlePluginKey, true));
}

export function colDragStart(
  editor: Editor,
  event: { clientX: number; currentTarget: EventTarget & Element; dataTransfer: DataTransfer | null },
) {
  tableDragStart(editor, "col", { ...event, clientY: 0 });
}

export function rowDragStart(
  editor: Editor,
  event: { clientY: number; currentTarget: EventTarget & Element; dataTransfer: DataTransfer | null },
) {
  tableDragStart(editor, "row", { ...event, clientX: 0 });
}

export function tableDragEnd(editor: Editor) {
  const view = getStorage(editor)?.view;
  if (!view?.state) return;
  view.state = { ...view.state, draggingState: undefined };
  view.emitUpdate();
  editor.view.dispatch(editor.state.tr.setMeta(tableHandlePluginKey, false));
}
